/**
 * Space effects feature (M2 plan §6 M2-20, order `FEATURE_ORDER.spaceFx`): career mode only. An M2-owned particle
 * system on the asset lane's VFX materials (`createVfxMaterials`, the fx feature's pools and emitters reused
 * read-only) that turns the M2 space ring into light (effects.ts), runs the held tools' continuous effects and draws
 * the tool beams as direct streaks (beams.ts); `tetherstone` events flicker the gate (`env.setGateFlicker`).
 * Particles live in float64 world (D-011) on the interpolated render clock, like the combat effects.
 * `bus 'quality'` rebuilds the pools at the preset's cap. `frameSpaceFx` is allocation-free (D-014).
 */
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { SIM_DT } from '../../core/loop'
import { logger } from '../../core/logger'
import type { QualityPreset } from '../../render/contracts'
import { createVfxMaterials } from '../../render/materials'
import type { M2Runtime } from '../../space/contracts'
import { Pos64, PrevPos64 } from '../../world/components'
import { poolCapacities } from '../fx/config'
import {
  beginParticleFrame,
  clearParticleSystem,
  createParticleSystem,
  disposeParticleSystem,
  drawnInstances,
  endParticleFrame,
  liveParticles,
  setParticleCapacities,
  stepParticleSystem,
  type ParticleSystem,
} from '../fx/particles'
import { pushBeams } from './beams'
import { loadM2FxConfig, type M2FxConfig } from './config'
import { createSpaceFxReader, emitContinuous, readSpaceFx, type SpaceFxReader } from './effects'

/** Seed salt so the M2 effects RNG never shares a sequence with the sim's or the combat effects'. */
const M2FX_SEED_SALT = 0x2f6d7a91
/** A render clock this far behind the particles' means the sim clock was reset (new career): drop the particles. */
const CLOCK_RESET_SECONDS = 1
/** Continuous effects never catch up more than this (s) after a long frame. */
const MAX_EMIT_DT = 0.25

export interface SpaceFxStats {
  particles: number
  drawn: number
  /** Beam streaks drawn this frame. */
  beams: number
  /** Effects triggered from space events so far. */
  triggered: number
}

export interface SpaceFxState {
  readonly m2: M2Runtime
  readonly system: ParticleSystem
  readonly reader: SpaceFxReader
  /** Render time of the last frame (continuous emission covers the time since). */
  lastTime: number
  unsubscribe: () => void
}

export interface SpaceFxFeature extends GameFeature {
  readonly config: M2FxConfig
  readonly stats: SpaceFxStats
  readonly state: SpaceFxState | null
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

export function m2PoolCapacities(config: M2FxConfig, preset: QualityPreset): ReturnType<typeof poolCapacities> {
  const p = config.particles
  return poolCapacities(p.caps[preset], p.weights, p.minPerPool)
}

/** Builds the particle system and the event reader for `m2` (allocates; once per career session). */
export function createSpaceFxState(ctx: GameContext, m2: M2Runtime, config: M2FxConfig): SpaceFxState {
  const sim = m2.space.sim
  const vfx = createVfxMaterials(ctx.lib)
  const system = createParticleSystem(vfx, vfx.recipes, m2PoolCapacities(config, ctx.quality), (sim.seed ^ M2FX_SEED_SALT) >>> 0)
  system.root.name = 'm2fx:particles'
  system.time = sim.time
  ctx.scene.add(system.root)
  return {
    m2,
    system,
    reader: createSpaceFxReader(m2.space.events),
    lastTime: sim.time,
    unsubscribe: ctx.bus.on('quality', (preset) => setParticleCapacities(system, m2PoolCapacities(config, preset))),
  }
}

/** Per frame (allocation-free). */
export function frameSpaceFx(ctx: GameContext, s: SpaceFxState, config: M2FxConfig, stats: SpaceFxStats, alpha: number): void {
  const space = s.m2.space
  const sim = space.sim
  const back = (alpha - 1) * SIM_DT
  const now = sim.time + back
  const sys = s.system
  if (now < sys.time - CLOCK_RESET_SECONDS) {
    clearParticleSystem(sys, now)
    s.lastTime = now
  }
  readSpaceFx(s.reader, sys, space, config, ctx.env, now)
  let dt = now - s.lastTime
  dt = dt < 0 ? 0 : dt > MAX_EMIT_DT ? MAX_EMIT_DT : dt
  s.lastTime = now
  emitContinuous(sys, space.salvage, config, dt, now)
  stepParticleSystem(sys, now)
  beginParticleFrame(sys, ctx.rig.pos)
  // The muzzle end follows the interpolated player ship (the readouts hold its tick position).
  const p = sim.playerEid
  let ox = 0
  let oy = 0
  let oz = 0
  if (p >= 0) {
    const k = 1 - alpha
    ox = (PrevPos64.x[p] - Pos64.x[p]) * k
    oy = (PrevPos64.y[p] - Pos64.y[p]) * k
    oz = (PrevPos64.z[p] - Pos64.z[p]) * k
  }
  stats.beams = pushBeams(sys, config.beams, space.rocks.readout, space.salvage.readout, ox, oy, oz, ctx.rig.pos)
  endParticleFrame(sys)
  stats.particles = liveParticles(sys)
  stats.drawn = drawnInstances(sys)
  stats.triggered = s.reader.triggered
}

export function createSpaceFxFeature(config: M2FxConfig = loadM2FxConfig()): SpaceFxFeature {
  let state: SpaceFxState | null = null
  const stats: SpaceFxStats = { particles: 0, drawn: 0, beams: 0, triggered: 0 }

  function attach(ctx: GameContext): void {
    if (state || ctx.mode !== 'career') return
    const m2 = ctx.services.maybe('m2')
    if (!m2) return
    state = createSpaceFxState(ctx, m2, config)
    logger.info('Space effects: attached to the M2 runtime')
  }

  const feature: SpaceFxFeature = {
    name: 'spaceFx',
    order: FEATURE_ORDER.spaceFx,
    config,
    stats,
    get state(): SpaceFxState | null {
      return state
    },
    init(ctx: GameContext): void {
      attach(ctx)
    },
    frame(ctx: GameContext, f: FrameInfo): void {
      if (!state) {
        attach(ctx)
        if (!state) return
      }
      frameSpaceFx(ctx, state, config, stats, f.alpha)
    },
    dispose(): void {
      const s = state
      if (!s) return
      s.unsubscribe()
      disposeParticleSystem(s.system)
      state = null
    },
  }
  return feature
}

const feature: SpaceFxFeature = createSpaceFxFeature()
export default feature
